import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface Page {
  id: string;
  title: string;
  url: string;
  html: string;
  createdAt: string;
  updatedAt: string;
  status: 'captured' | 'processing' | 'optimized' | 'error';
  analysis?: {
    seoScore?: number;
    geoScore?: number;
  };
}

const Pages: React.FC = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('list');

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/pages');
      setPages(response.data.pages || []);
    } catch (err) {
      console.error('Error fetching pages:', err);
      setError('Failed to load pages');
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (pageId: string) => {
    navigate(`/dashboard/pages/edit/${pageId}`);
  };

  const handleDelete = async (pageId: string, pageTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete "${pageTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/pages/${pageId}`);
      setPages(pages.filter(page => page.id !== pageId));
      toast.success('Page deleted successfully');
    } catch (err) {
      console.error('Error deleting page:', err);
      toast.error('Failed to delete page');
    }
  };

  const handleRefresh = () => {
    fetchPages();
  };

  const filteredPages = pages.filter(page => {
    const matchesSearch = page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         page.url.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || page.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      captured: 'bg-success',
      processing: 'bg-warning',
      optimized: 'bg-info',
      error: 'bg-danger'
    };
    return (
      <span className={`badge ${statusClasses[status as keyof typeof statusClasses] || 'bg-secondary'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading pages...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Pages Management</h2>
          <p className="text-muted mb-0">Manage your captured and optimized pages</p>
        </div>
        <div className="btn-group">
          <button
            className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            <i className="fas fa-list"></i>
          </button>
          <button
            className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setViewMode('cards')}
            title="Card view"
          >
            <i className="fas fa-th"></i>
          </button>
          <button className="btn btn-outline-secondary" onClick={handleRefresh}>
            <i className="fas fa-sync-alt me-2"></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search pages by title or URL..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="captured">Captured</option>
            <option value="processing">Processing</option>
            <option value="optimized">Optimized</option>
            <option value="error">Error</option>
          </select>
        </div>
        <div className="col-md-3">
          <div className="text-muted">
            Showing {filteredPages.length} of {pages.length} pages
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <button className="btn btn-sm btn-outline-danger ms-2" onClick={handleRefresh}>
            Try Again
          </button>
        </div>
      )}

      {/* Pages List */}
      {filteredPages.length === 0 ? (
        <div className="text-center py-5">
          <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
          <h5>No pages found</h5>
          <p className="text-muted">
            {searchTerm || filterStatus !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'You haven\'t captured any pages yet. Start by capturing a page to get started.'
            }
          </p>
        </div>
      ) : viewMode === 'list' ? (
        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '30%' }}>Page Title</th>
                    <th style={{ width: '35%' }}>URL</th>
                    <th style={{ width: '10%' }}>Status</th>
                    <th style={{ width: '10%' }}>SEO Score</th>
                    <th style={{ width: '10%' }}>GEO Score</th>
                    <th style={{ width: '10%' }}>Created</th>
                    <th style={{ width: '15%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPages.map((page) => (
                    <tr key={page.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <i className="fas fa-file-alt text-muted me-2"></i>
                          <div>
                            <div className="fw-medium text-truncate" style={{ maxWidth: '200px' }} title={page.title}>
                              {page.title || 'Untitled Page'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="text-truncate" style={{ maxWidth: '250px' }} title={page.url}>
                          <i className="fas fa-link text-muted me-1"></i>
                          <a 
                            href={page.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-decoration-none"
                          >
                            {page.url}
                          </a>
                        </div>
                      </td>
                      <td>
                        {getStatusBadge(page.status)}
                      </td>
                      <td>
                        <div className="text-center">
                          {page.analysis?.seoScore ? (
                            <span className={`badge ${page.analysis.seoScore >= 80 ? 'bg-success' : page.analysis.seoScore >= 60 ? 'bg-warning' : 'bg-danger'}`}>
                              {page.analysis.seoScore}/100
                            </span>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="text-center">
                          {page.analysis?.geoScore ? (
                            <span className={`badge ${page.analysis.geoScore >= 80 ? 'bg-success' : page.analysis.geoScore >= 60 ? 'bg-warning' : 'bg-danger'}`}>
                              {page.analysis.geoScore}/100
                            </span>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <small className="text-muted">
                          <i className="fas fa-calendar me-1"></i>
                          {new Date(page.createdAt).toLocaleDateString()}
                        </small>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm" role="group">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleEdit(page.id)}
                            title="Edit page"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(page.id, page.title || 'Untitled Page')}
                            title="Delete page"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          {filteredPages.map((page) => (
            <div key={page.id} className="col-lg-6 col-xl-4 mb-4">
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="card-title mb-0 text-truncate" title={page.title}>
                      {page.title || 'Untitled Page'}
                    </h6>
                    {getStatusBadge(page.status)}
                  </div>
                  
                  <p className="card-text text-muted small mb-3">
                    <i className="fas fa-link me-1"></i>
                    <span className="text-truncate d-block" title={page.url}>
                      {page.url}
                    </span>
                  </p>

                  <div className="row text-center mb-3">
                    <div className="col-6">
                      <small className="text-muted">SEO Score</small>
                      <div className="fw-bold">
                        {page.analysis?.seoScore ? `${page.analysis.seoScore}/100` : 'N/A'}
                      </div>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">GEO Score</small>
                      <div className="fw-bold">
                        {page.analysis?.geoScore ? `${page.analysis.geoScore}/100` : 'N/A'}
                      </div>
                    </div>
                  </div>

                  <div className="text-muted small mb-3">
                    <i className="fas fa-calendar me-1"></i>
                    Created: {new Date(page.createdAt).toLocaleDateString()}
                  </div>
                </div>

                <div className="card-footer bg-transparent">
                  <div className="btn-group w-100" role="group">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => handleEdit(page.id)}
                      title="Edit page"
                    >
                      <i className="fas fa-edit me-1"></i>
                      Edit
                    </button>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleDelete(page.id, page.title || 'Untitled Page')}
                      title="Delete page"
                    >
                      <i className="fas fa-trash me-1"></i>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Pages;
